<script lang="ts">
  import {
    CircleEllipsis,
    MessageSquareText,
    Copy,
    Trash2
  } from "lucide-svelte"
  import { toast, xhr } from "$app/utils"
  import { destroy } from "../Task/api"
  import { needsUpdate } from "$app/utils/goals.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import InlineInput from "$app/components/InlineInput.svelte"
  import EditGoalStatus from "../EditGoalStatus.svelte"
  import ProgressPanel from "../ProgressPanel.svelte"
  import EditAssignee from "../Task/EditAssignee.svelte"
  import Dropdown from "$app/components/Dropdown.svelte"
  import { appStore } from "$app/store.svelte"
  import DatePicker from "$app/components/DatePicker.svelte"

  interface Props {
    task: TaskType
    oncreate?: (task: TaskType) => void
    ondelete?: (taskId: number) => void
  }

  let { task = $bindable(), oncreate, ondelete }: Props = $props()

  const plan = appStore.plan
  const last_meeting_date: Date = appStore.last_meeting_date
  const today: Date = appStore.today

  task.needsUpdate = needsUpdate(task, last_meeting_date, today)

  let openProgressDropdown = $state(false)
  let openTaskDropdown = $state(false)

  let expected_at = $state(null)
  try {
    expected_at = new Date(`${task.expected_at}T00:00:00`)
  } catch (e) {}

  function updateTask(data: TaskType): void {
    const idx = appStore.tasks.findIndex((t) => t.id === task.id)
    task.created_at = new Date(data.created_at)
    task.needsUpdate = needsUpdate(task, last_meeting_date, today)
    task.progress_date = new Date(data.created_at)
    appStore.tasks[idx] = task
  }

  function openTaskDetail() {
    //dispatch("openTaskModal", { taskId: task.id })
  }

  async function duplicateTask(): Promise<void> {
    let newTask = null
    openTaskDropdown = false

    try {
      const resp = await xhr.post(
        `/plans/${task.plan_id}/tasks/duplicate.json`,
        { id: task.id }
      )
      newTask = resp.data
      oncreate(newTask)
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error duplicating the task", "error")
    }
  }

  async function deleteTask(): Promise<void> {
    openTaskDropdown = false

    setTimeout(async () => {
      if (confirm("Are you sure you want to delete this task?")) {
        try {
          await destroy(task)
          ondelete(task.id)
        } catch (e) {
          if (typeof Honeybadger.notify !== "undefined") {
            Honeybadger.notify(e)
          }
          toast("There was an error deleting the task", "error")
        }
      }
    })
  }

  let saving = false

  async function updateExpectedAt(expected_at: Date): Promise<void> {
    saving = true
    try {
      const url = `/plans/${task.plan_id}/tasks/${task.id}.json`
      const res = await xhr.patch(url, { task: { expected_at } })

      dispatch("updateTask", { ...res.data })
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating the Expected By date", "error")
    } finally {
      open = false
      saving = false
    }
  }
</script>

<div
  class="border-l-4 border-blue-800 shadow-sm"
  ondblclick={openTaskDetail}
  data-name="task"
  role="button"
  tabindex="0"
>
  <div class="py-2 border-gray-300 border-b border-t flex items-center">
    <div class="flex flex-wrap w-full ml-3 items-center">
      <div class="w-7/12 lg:w-4/12 text-left text-sm pr-2">
        <InlineInput
          onsuccess={(d) => updateTask(d)}
          bind:value={task.name}
          postURL={`/plans/${task.plan_id}/tasks/${task.id}.json`}
          name="name"
          model="task"
        />
      </div>

      <div class="w-5/12 lg:w-3/12 items-center justify-between flex gap-2">
        <p
          class="text-xs text-gray-800 flex items-center mr-3"
          data-name="task-assignee-container"
        >
          <EditAssignee
            {task}
            class="w-20"
            onupdate={(data) => {
              updateTask(data)
            }}
          />
        </p>
        <Dropdown bind:open={openProgressDropdown}>
          {#snippet button()}
            <span
              class="focus:outline-none text-blue-400"
              class:text-red-500={task.needsUpdate}
            >
              <MessageSquareText
                class="size-6  {task.needsUpdate ? 'text-red-500' : ''}"
              />
            </span>
          {/snippet}

          <div>
            <div
              class="origin-top-right absolute -left-48 mt-2 w-96 lg:w-120 bg-white focus:outline-none focus:outline-none z-10"
            >
              <ProgressPanel
                goal={task}
                type="task"
                oncreate={(data) => {
                  openProgressDropdown = false
                  toast("Progress added", "success")
                  updateTask(data)
                }}
                onclose={() => (openProgressDropdown = false)}
              />
            </div>
          </div>
        </Dropdown>
      </div>

      <div class="w-5/12 lg:w-2/12 flex items-center justify-center">
        <EditGoalStatus
          goalOrTask={task}
          type="Task"
          small={true}
          editable={true}
          show_border={false}
          onupdate={(data) => updateTask(data)}
        />
      </div>

      <div class="w-5/12 lg:w-2/12 text-center">
        <p class="text-xs text-gray-800">
          <DatePicker
            date={expected_at}
            onchange={(detail) => {
              updateExpectedAt(detail)
            }}
          />
        </p>
      </div>

      <div class="w-2/12 lg:w-1/12 text-center">
        <div class="relative inline-block text-left">
          <Dropdown bind:open={openTaskDropdown}>
            {#snippet button()}
              <span class="text-gray-700">
                <CircleEllipsis class="text-gray-400 size-7" />
              </span>
            {/snippet}

            <div>
              <div
                class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 focus:outline-none z-10"
                role="menu"
              >
                <div class="py-1" role="none">
                  <button
                    class="btn-dropdown"
                    role="menuitem"
                    onclick={duplicateTask}
                  >
                    <Copy class="text-gray-400 size-4 mr-3" />
                    Duplicate
                  </button>
                  <button
                    class="btn-dropdown"
                    role="menuitem"
                    onclick={deleteTask}
                  >
                    <Trash2 class="text-gray-400 size-4 mr-3" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </Dropdown>
        </div>
      </div>
    </div>
  </div>
</div>
